import { Fragment, useEffect, useMemo, useState } from "react";
import { defineMessages, MessageDescriptor, useIntl } from "react-intl";
import { offset, useClick, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { IconSelector } from "@tabler/icons-react";
import { useIsMounted } from "@ct-react/core";
import { AnyArticleSortInput } from "@shared/graphql";
import { ArticleType } from "@shared/url";
import "./sort.scss";

type KeysOfUnion<T> = T extends T ? keyof T : never;

const transDefs = defineMessages({
  prefix: { id: "search.sort.prefix", defaultMessage: "Trier par:" },
  default: { id: "search.sort.default", defaultMessage: "Le plus récent" }
});

const sortableDefs = defineMessages({
  title: { id: "search.sort.name", defaultMessage: "Nom (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  price: { id: "search.sort.price", defaultMessage: "Prix (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  rooms: { id: "search.sort.rooms", defaultMessage: "Pièces (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  bedrooms: { id: "search.sort.bedrooms", defaultMessage: "Chambres (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  guests: { id: "search.sort.guests", defaultMessage: "Personnes (ordre {sort, select, DESC {décroissant} other {croissant}})" },
  surface: { id: "search.sort.surface", defaultMessage: "Superficie du bien (ordre {sort, select, DESC {décroissant} other {croissant}})" }
}) satisfies Partial<Record<KeysOfUnion<AnyArticleSortInput>, MessageDescriptor>>;

const display: Record<ArticleType | "sold", KeysOfUnion<AnyArticleSortInput>[]> = {
  [ArticleType.RENTAL]: [ "price", "bedrooms", "guests", "title" ],
  [ArticleType.SALE]: [ "price", "rooms", "surface" ],
  sold: [ "title" ],
}

type Props = {
  type: ArticleType | "sold";
  criteria: AnyArticleSortInput;
  onCriteriaChange: (value: AnyArticleSortInput) => void;
}

const SearchSort = ({ type, criteria, onCriteriaChange }: Props) => {

  const isMounted = useIsMounted();
  const intl = useIntl();
  const [ open, setOpen ] = useState(false);

  const { context, refs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    strategy: "absolute",
    placement: "bottom-end",
    middleware: [ offset(8) ]
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context, { outsidePress: true, ancestorScroll: true })
  ]);

  useEffect(() => {
    (isMounted && open) && setOpen(false);
  }, [ criteria ]);

  const selectedValue = useMemo(() => {
    const [ key, sort ] = Object.entries(criteria)[0];
    //@ts-ignore
    return intl.formatMessage(sortableDefs[key] || transDefs.default, { sort });
  }, [ criteria ]);

  const onSelect = (key: string, sort: "ASC" | "DESC") => onCriteriaChange({ [key]: sort });

  return (
    <>
      <div ref={refs.setReference}
           {...getReferenceProps()}
           className="sort-input">
        <span className="prefix">{intl.formatMessage(transDefs.prefix)}</span>
        <span className="value ellipsed">{selectedValue}</span>
        <IconSelector className="s-icon" />
      </div>
      {open &&
        <div ref={refs.setFloating}
             {...getFloatingProps()}
             className="sort-selector"
             style={floatingStyles}>
          <ul>
            <li onClick={() => onSelect("publication", "DESC")}>
              {intl.formatMessage(transDefs.default)}
            </li>
            {display[type].map((key: KeysOfUnion<AnyArticleSortInput>, index: number) => {
              //@ts-ignore
              const transDef = sortableDefs[key]!;
              return (
                <Fragment key={index}>
                  <li onClick={() => onSelect(key, "ASC")}>
                    {intl.formatMessage(transDef, { sort: "ASC" })}
                  </li>
                  <li onClick={() => onSelect(key, "DESC")}>
                    {intl.formatMessage(transDef, { sort: "DESC" })}
                  </li>
                </Fragment>);
            })}
          </ul>
        </div>
      }
    </>);
}

export default SearchSort;
